import type { CytoElement, DNSEdge, DNSNode, GodEnrichmentResult } from '@/types/dns';

/**
 * Strip characters that are unsafe in Cytoscape node IDs / selector expressions.
 * Keeps alphanumeric, dots, hyphens, underscores, and colons (all valid in IDs
 * and safe in CSS-style selectors). Everything else becomes an underscore so the
 * ID remains human-readable while being selector-safe.
 */
function safeId(value: string): string {
  return value.replace(/[^a-zA-Z0-9.\-_:]/g, '_');
}

/**
 * Convert a god daemon enrichment result into Cytoscape element definitions.
 *
 * Node ID conventions:
 *   domain -> "domain-{domain}"
 *   ip     -> "ip-{ip}"
 *   asn    -> "asn-AS{number}"
 *   ns     -> "ns-{hostname}"
 *   mx     -> "mx-{hostname}"
 *   cname  -> "cname-{hostname}"
 *
 * ASN compound grouping: IP nodes get parent="asn-AS{n}" when asn is present.
 */
export function parseEnrichmentToElements(
  result: GodEnrichmentResult,
  rootTarget: string,
): CytoElement[] {
  const elements: CytoElement[] = [];
  const addedIds = new Set<string>();

  function addNode(node: DNSNode): void {
    if (addedIds.has(node.id)) return;
    addedIds.add(node.id);
    elements.push({ group: 'nodes', data: node });
  }

  function addEdge(edge: DNSEdge): void {
    if (addedIds.has(edge.id)) return;
    addedIds.add(edge.id);
    elements.push({ group: 'edges', data: edge });
  }

  const threatScore = result.threat_score ?? 0;
  const rootId = result.domain
    ? `domain-${safeId(result.domain)}`
    : `ip-${safeId(result.ip ?? rootTarget)}`;

  // --- Root node (domain or IP) ---
  if (result.domain) {
    addNode({
      id: rootId,
      type: 'domain',
      label: result.domain,
      threatScore,
      country: result.country,
      org: result.asn_org,
      abuse_score: result.abuse_score,
      feeds: result.feeds,
      raw: result as Record<string, unknown>,
    });
  } else if (result.ip) {
    addNode({
      id: rootId,
      type: 'ip',
      label: result.ip,
      threatScore,
      country: result.country,
      org: result.asn_org,
      raw: result as Record<string, unknown>,
    });
  }

  // --- A records ---
  for (const ip of result.a_records ?? []) {
    const ipId = `ip-${safeId(ip)}`;
    let parentId: string | undefined;

    // ASN compound node
    if (result.asn) {
      const asnId = `asn-${safeId(result.asn)}`;
      parentId = asnId;
      addNode({
        id: asnId,
        type: 'asn',
        label: `${result.asn}${result.asn_org ? ` — ${result.asn_org}` : ''}`,
        threatScore: 0,
      });
    }

    addNode({
      id: ipId,
      type: 'ip',
      label: ip,
      threatScore: 0, // IP-level threat score requires separate enrichment (Phase 2+)
      parent: parentId,
      country: result.country,
      org: result.asn_org,
    });

    addEdge({
      id: `edge-${rootId}-${ipId}-A`,
      source: rootId,
      target: ipId,
      type: 'A',
    });
  }

  // --- AAAA records ---
  for (const ip of result.aaaa_records ?? []) {
    const ipId = `ip-${safeId(ip)}`;
    addNode({
      id: ipId,
      type: 'ip',
      label: ip,
      threatScore: 0,
    });
    addEdge({
      id: `edge-${rootId}-${ipId}-AAAA`,
      source: rootId,
      target: ipId,
      type: 'AAAA',
    });
  }

  // --- NS records ---
  for (const ns of result.ns_records ?? []) {
    const nsId = `ns-${safeId(ns)}`;
    addNode({
      id: nsId,
      type: 'ns',
      label: ns,
      threatScore: 0,
    });
    addEdge({
      id: `edge-${rootId}-${nsId}-NS`,
      source: rootId,
      target: nsId,
      type: 'NS',
    });
  }

  // --- MX records ---
  for (const mx of result.mx_records ?? []) {
    const mxHost = typeof mx === 'string' ? mx : mx.host;
    const mxId = `mx-${safeId(mxHost)}`;
    addNode({
      id: mxId,
      type: 'mx',
      label: mxHost,
      threatScore: 0,
    });
    addEdge({
      id: `edge-${rootId}-${mxId}-MX`,
      source: rootId,
      target: mxId,
      type: 'MX',
    });
  }

  // --- CNAME records ---
  for (const cname of result.cname_records ?? []) {
    const cnameId = `cname-${safeId(cname)}`;
    addNode({
      id: cnameId,
      type: 'cname',
      label: cname,
      threatScore: 0,
    });
    addEdge({
      id: `edge-${rootId}-${cnameId}-CNAME`,
      source: rootId,
      target: cnameId,
      type: 'CNAME',
    });
  }

  return elements;
}
